import { useState, useEffect, useMemo, useRef } from 'react';
import {
  Box, Typography, Button, Chip, CircularProgress, Snackbar, Alert,
  Table, TableBody, TableCell, TableHead, TableRow,
  Dialog, DialogContent, Divider, Stack
} from '@mui/material';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import PendingActionsOutlinedIcon from '@mui/icons-material/PendingActionsOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import CloseIcon from '@mui/icons-material/Close';

import adminAxios from '../../utils/adminAxios';

const dinero = (n) => '$' + (Number(n) || 0).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const nombreDe = (u) => u ? `${u.name || ''} ${u.apellido || ''}`.trim() || '—' : '—';

const cabeceraSx = { fontSize: 12, fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: 0.4, borderBottom: '1px solid #F1F5F9' };
const celdaSx = { borderBottom: '1px solid #F8FAFC', py: 1.5 };

const StatCard = ({ icon: Icon, label, value, color, bg }) => (
  <Box sx={{ flex: 1, minWidth: 180, bgcolor: 'white', border: '1px solid #F1F5F9', borderRadius: 3, p: 2.5, display: 'flex', alignItems: 'center', gap: 2 }}>
    <Box sx={{ width: 46, height: 46, borderRadius: 2, bgcolor: bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Icon sx={{ color }} />
    </Box>
    <Box>
      <Typography sx={{ fontSize: 22, fontWeight: 800, color: 'primary.main', lineHeight: 1 }}>{value}</Typography>
      <Typography sx={{ fontSize: 13, color: '#737791', mt: 0.5 }}>{label}</Typography>
    </Box>
  </Box>
);

const estadoChip = (estado) => {
  if (estado === 'emitida') return <Chip label="Facturado" size="small" sx={{ bgcolor: '#D6F3E8', color: '#1B7A5A', fontWeight: 700, fontSize: 11 }} />;
  return <Chip label="Pendiente" size="small" sx={{ bgcolor: '#FFF1D6', color: '#A8740F', fontWeight: 700, fontSize: 11 }} />;
};

const DatoFiscal = ({ label, value }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, py: 0.6 }}>
    <Typography sx={{ fontSize: 13, color: '#737791' }}>{label}</Typography>
    <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#0E2465', textAlign: 'right', wordBreak: 'break-word' }}>{value || '—'}</Typography>
  </Box>
);

const Facturacion = () => {
  const [facturas, setFacturas] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [toast, setToast] = useState({ open: false, msg: '', severity: 'success' });
  const [dialog, setDialog] = useState(null); // booking en edición
  const [pdf, setPdf] = useState(null);
  const [xml, setXml] = useState(null);
  const [guardando, setGuardando] = useState(false);
  const pdfRef = useRef(null);
  const xmlRef = useRef(null);

  const cargar = () => {
    setCargando(true);
    adminAxios.get('/api/admin/facturas')
      .then(res => setFacturas(res.data || []))
      .catch(() => setToast({ open: true, msg: 'No se pudieron cargar las facturas.', severity: 'error' }))
      .finally(() => setCargando(false));
  };
  useEffect(cargar, []);

  const stats = useMemo(() => ({
    solicitadas: facturas.filter(f => f.factura?.estado === 'solicitada').length,
    emitidas: facturas.filter(f => f.factura?.estado === 'emitida').length,
  }), [facturas]);

  const abrir = (booking) => { setDialog(booking); setPdf(null); setXml(null); };
  const cerrar = () => { if (!guardando) setDialog(null); };

  const emitir = async () => {
    if (!dialog) return;
    const yaEmitida = dialog.factura?.estado === 'emitida';
    if (!yaEmitida && (!pdf || !xml)) {
      setToast({ open: true, msg: 'Sube el PDF y el XML de la factura.', severity: 'error' });
      return;
    }
    setGuardando(true);
    try {
      const fd = new FormData();
      if (pdf) fd.append('pdf', pdf);
      if (xml) fd.append('xml', xml);
      await adminAxios.patch(`/api/admin/facturas/${dialog._id}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setToast({ open: true, msg: 'Factura subida. El cliente ya puede descargarla.', severity: 'success' });
      setDialog(null);
      cargar();
    } catch (err) {
      setToast({ open: true, msg: err.response?.data?.msg || 'No se pudo subir la factura.', severity: 'error' });
    } finally {
      setGuardando(false);
    }
  };

  const df = dialog?.factura?.datosFiscales || {};

  return (
    <Box>
      {/* Stats */}
      <Stack direction="row" spacing={2} sx={{ mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <StatCard icon={ReceiptLongOutlinedIcon} label="Total solicitudes" value={facturas.length} color="#7495F8" bg="#EEF2FF" />
        <StatCard icon={PendingActionsOutlinedIcon} label="Pendientes por emitir" value={stats.solicitadas} color="#A8740F" bg="#FFF1D6" />
        <StatCard icon={CheckCircleOutlineIcon} label="Facturadas" value={stats.emitidas} color="#1B7A5A" bg="#D6F3E8" />
      </Stack>

      <Box sx={{ bgcolor: 'white', border: '1px solid #F1F5F9', borderRadius: 3, p: 1 }}>
        {cargando ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={cabeceraSx}>Servicio</TableCell>
                <TableCell sx={cabeceraSx}>Cliente</TableCell>
                <TableCell sx={cabeceraSx}>Datos fiscales</TableCell>
                <TableCell sx={cabeceraSx} align="right">Monto</TableCell>
                <TableCell sx={cabeceraSx} align="center">Estado</TableCell>
                <TableCell sx={cabeceraSx} align="center">Acción</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {facturas.map(b => (
                <TableRow key={b._id} hover>
                  <TableCell sx={celdaSx}>
                    <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'primary.main' }}>{b.servicioNombre || 'Servicio'}</Typography>
                    <Typography sx={{ fontSize: 11, color: '#94A3B8' }}>{b.fecha} {b.hora}</Typography>
                  </TableCell>
                  <TableCell sx={celdaSx}>
                    <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'primary.main' }}>{nombreDe(b.cliente)}</Typography>
                    <Typography sx={{ fontSize: 11, color: '#94A3B8' }}>{b.cliente?.email}</Typography>
                  </TableCell>
                  <TableCell sx={celdaSx}>
                    <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#0E2465' }}>{b.factura?.datosFiscales?.rfc || '—'}</Typography>
                    <Typography sx={{ fontSize: 11, color: '#94A3B8' }}>
                      {b.factura?.datosFiscales?.razonSocial || b.factura?.datosFiscales?.nombre || ''}
                    </Typography>
                  </TableCell>
                  <TableCell sx={celdaSx} align="right"><strong>{dinero(b.costoServicio)}</strong></TableCell>
                  <TableCell sx={celdaSx} align="center">{estadoChip(b.factura?.estado)}</TableCell>
                  <TableCell sx={celdaSx} align="center">
                    <Button size="small" variant={b.factura?.estado === 'emitida' ? 'outlined' : 'contained'} disableElevation
                      onClick={() => abrir(b)}
                      startIcon={<CloudUploadOutlinedIcon sx={{ fontSize: 15 }} />}
                      sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 2, fontSize: 12,
                        ...(b.factura?.estado === 'emitida'
                          ? { borderColor: '#E2E8F0', color: '#0E2465' }
                          : { bgcolor: '#0E2465', color: 'white', '&:hover': { bgcolor: '#0a1a4a' } }) }}>
                      {b.factura?.estado === 'emitida' ? 'Ver / reemplazar' : 'Subir factura'}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {facturas.length === 0 && (
                <TableRow><TableCell colSpan={6} sx={{ ...celdaSx, textAlign: 'center', color: '#94A3B8', py: 5 }}>
                  No hay facturas solicitadas.
                </TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </Box>

      {/* Dialog subir factura */}
      <Dialog open={!!dialog} onClose={cerrar} maxWidth="xs" fullWidth slotProps={{ paper: { sx: { borderRadius: '20px' } } }}>
        {dialog && (
          <DialogContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Typography sx={{ fontSize: 18, fontWeight: 800, color: 'primary.main' }}>Emitir factura</Typography>
              <Button onClick={cerrar} sx={{ minWidth: 0, p: 0.5, color: '#737791' }}><CloseIcon sx={{ fontSize: 20 }} /></Button>
            </Box>
            <Typography sx={{ fontSize: 13.5, color: '#737791', mb: 2 }}>
              {dialog.servicioNombre} — {dinero(dialog.costoServicio)}
            </Typography>

            <Typography sx={{ fontSize: 12, fontWeight: 800, color: '#0E2465', letterSpacing: 0.4, mb: 1 }}>DATOS FISCALES DEL CLIENTE</Typography>
            <Box sx={{ bgcolor: '#F8FAFC', borderRadius: '12px', p: 2, mb: 3 }}>
              <DatoFiscal label="RFC" value={df.rfc} />
              <DatoFiscal label="Razón social" value={df.razonSocial || df.nombre} />
              <DatoFiscal label="Régimen fiscal" value={df.regimenFiscal} />
              <DatoFiscal label="Uso de CFDI" value={df.usoCFDI} />
              <DatoFiscal label="Código postal" value={df.cp} />
              <DatoFiscal label="Dirección" value={df.direccionFiscal} />
            </Box>

            <Divider sx={{ borderColor: '#F1F5F9', mb: 2 }} />

            <Typography sx={{ fontSize: 12, fontWeight: 800, color: '#0E2465', letterSpacing: 0.4, mb: 1 }}>ARCHIVOS DE LA FACTURA</Typography>
            {[
              { tipo: 'PDF', ref: pdfRef, file: pdf, set: setPdf, accept: 'application/pdf', existente: dialog.factura?.pdf },
              { tipo: 'XML', ref: xmlRef, file: xml, set: setXml, accept: 'text/xml,application/xml,.xml', existente: dialog.factura?.xml },
            ].map(({ tipo, ref, file, set, accept, existente }) => (
              <Box key={tipo} sx={{ mb: 1.5 }}>
                <Box
                  onClick={() => ref.current?.click()}
                  sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1.5, borderRadius: '12px',
                    border: '1.5px dashed #CBD5E1', cursor: 'pointer', '&:hover': { borderColor: '#7495F8', bgcolor: '#F8FAFC' } }}>
                  <InsertDriveFileOutlinedIcon sx={{ color: file ? '#1B7A5A' : '#94A3B8' }} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#0E2465' }}>{tipo}</Typography>
                    <Typography noWrap sx={{ fontSize: 12, color: file ? '#1B7A5A' : '#94A3B8' }}>
                      {file ? file.name : (existente?.url ? `Actual: ${existente.nombre || 'archivo'}` : `Selecciona el ${tipo}`)}
                    </Typography>
                  </Box>
                  {existente?.url && !file && (
                    <Button size="small" component="a" href={existente.url} target="_blank" rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      sx={{ textTransform: 'none', fontSize: 11, color: '#7495F8' }}>Ver</Button>
                  )}
                </Box>
                <input ref={ref} type="file" hidden accept={accept} onChange={(e) => { set(e.target.files?.[0] || null); e.target.value = ''; }} />
              </Box>
            ))}

            <Button fullWidth variant="contained" disableElevation disabled={guardando} onClick={emitir}
              sx={{ mt: 2, bgcolor: '#0E2465', color: 'white', textTransform: 'none', fontWeight: 800, borderRadius: '12px', py: 1.4,
                '&:hover': { bgcolor: '#0a1a4a' }, '&.Mui-disabled': { bgcolor: '#94A3B8', color: 'white' } }}>
              {guardando ? <CircularProgress size={22} sx={{ color: 'white' }} /> : (dialog.factura?.estado === 'emitida' ? 'Actualizar factura' : 'Marcar como facturado')}
            </Button>
          </DialogContent>
        )}
      </Dialog>

      <Snackbar open={toast.open} autoHideDuration={4000} onClose={() => setToast(t => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={toast.severity} variant="filled" onClose={() => setToast(t => ({ ...t, open: false }))}>{toast.msg}</Alert>
      </Snackbar>
    </Box>
  );
};

export default Facturacion;
